import { useMemo } from "react";
import { Card, Empty, Typography } from "antd";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface RiskBand {
  band: "low" | "medium" | "high" | "unscored";
  count: number;
}

interface RiskBandHeatmapProps {
  bands: RiskBand[];
}

const BAND_LABEL: Record<RiskBand["band"], string> = {
  low: "Low risk",
  medium: "Medium risk",
  high: "High risk",
  unscored: "Not yet scored",
};

const BAND_FILL: Record<RiskBand["band"], string> = {
  low: "#1E79C8",
  medium: "#3892E1",
  high: "#CF1F42",
  unscored: "#56657a",
};

const ORDER: RiskBand["band"][] = ["low", "medium", "high", "unscored"];

export default function RiskBandHeatmap({ bands }: RiskBandHeatmapProps) {
  const data = useMemo(() => {
    const counts = new Map(bands.map((b) => [b.band, b.count]));
    return ORDER.map((band) => ({
      band,
      label: BAND_LABEL[band],
      count: counts.get(band) || 0,
    }));
  }, [bands]);

  const total = data.reduce((acc, row) => acc + row.count, 0);

  return (
    <Card
      title="Active project risk mix"
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          From most recent rubric scoring
        </Typography.Text>
      }
    >
      {total === 0 ? (
        <Empty description="No risk scores in this period" />
      ) : (
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
              aria-label="Bar chart of active project counts by risk band"
            >
              <XAxis type="number" allowDecimals={false} hide />
              <YAxis
                type="category"
                dataKey="label"
                width={120}
                tick={{ fontSize: 12, fill: "#092940" }}
              />
              <Tooltip
                formatter={(value) => {
                  const count = Number(value);
                  return [`${count} project${count === 1 ? "" : "s"}`, ""];
                }}
                contentStyle={{ borderRadius: 6, fontSize: 12 }}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} label={{ position: "right", fontSize: 12, fill: "#092940" }}>
                {data.map((row) => (
                  <Cell key={row.band} fill={BAND_FILL[row.band]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
